import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BranchesPage from "./pages/BranchesPage";
import SearchPage from "./pages/SearchPage";
import ProductComparePage from "./pages/ProductComparePage";
import SearchTest from "./components/SearchTest";
import CityPage from "./components/CityPage";

// import ProtectionLayer from "./components/ProtectionLayer";

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth !important;
    scroll-padding-top: 80px; /* Account for fixed header */
  }

  body {
    font-family: 'Roboto', sans-serif;
    line-height: 1.6;
    color: #333;
    background-color: #fff;
    overflow-x: hidden;
    scroll-behavior: smooth;
  }

  /* Enhanced smooth scrolling for all elements */
  * {
    scroll-behavior: smooth;
  }

  /* Smooth transitions for common interactions */
  a, button {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Page transition effects */
  .page-transition {
    animation: fadeInUp 0.6s ease-out;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Smooth hover effects */
  .smooth-hover {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .smooth-hover:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  }

  /* Scroll-triggered animations */
  .fade-in-scroll {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }

  .fade-in-scroll.visible {
    opacity: 1;
    transform: translateY(0);
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  ul {
    list-style: none;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    background: transparent !important;
  }

  /* Force footer to never have white background */
  footer,
  footer *,
  footer .container,
  footer > div,
  [class*="Footer"] {
    background: transparent !important;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const MainContent = styled.main`
  flex: 1;
  padding-bottom: 60px;

  @media (max-width: 768px) {
    padding-bottom: 40px;
  }
`;

// 404 Component for blocked admin routes
const NotFound = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      textAlign: "center",
      padding: "20px",
    }}
  >
    <h1 style={{ fontSize: "4rem", margin: "0", color: "#333" }}>404</h1>
    <h2 style={{ fontSize: "1.5rem", margin: "10px 0", color: "#666" }}>
      Page Not Found
    </h2>
    <p style={{ fontSize: "1rem", color: "#888", maxWidth: "500px" }}>
      The page you are looking for might have been removed, had its name
      changed, or is temporarily unavailable.
    </p>
    <a
      href="/"
      style={{
        marginTop: "20px",
        padding: "10px 20px",
        backgroundColor: "#ff6720",
        color: "white",
        textDecoration: "none",
        borderRadius: "5px",
        fontSize: "1rem",
      }}
    >
      Go to Homepage
    </a>
  </div>
);

// Custom component to handle smooth scroll to top and page transitions
const ScrollHandler = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Enable smooth scroll restoration
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    // Smooth scroll to top with enhanced animation
    const smoothScrollToTop = () => {
      const startPosition = window.pageYOffset;
      const startTime = performance.now();
      const duration = 800; // 800ms for smooth animation

      const easeInOutCubic = (t) => {
        return t < 0.5
          ? 4 * t * t * t
          : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      };

      const animateScroll = (currentTime) => {
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const easeProgress = easeInOutCubic(progress);

        const newPosition = startPosition * (1 - easeProgress);
        window.scrollTo(0, newPosition);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      // Start the animation
      requestAnimationFrame(animateScroll);
    };

    // Add page transition class
    document.body.classList.add("page-transition");

    // Smooth scroll to top with delay for better UX
    const scrollTimeout = setTimeout(() => {
      smoothScrollToTop();
    }, 100);

    return () => {
      clearTimeout(scrollTimeout);
      document.body.classList.remove("page-transition");
    };
  }, [location.pathname]);

  return <>{children}</>;
};

function App() {
  return (
    // <ProtectionLayer>
    <Router>
      <GlobalStyle />
      <ScrollHandler>
        <Routes>
          {/* Block all admin routes - show 404 */}
          <Route path="/admin" element={<NotFound />} />
          <Route path="/admin/*" element={<NotFound />} />

          {/* Public routes with header/footer */}
          <Route
            path="/"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <HomePage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/about"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <AboutPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/contact"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <ContactPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/branches"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <BranchesPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/collections/:category"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <ProductsPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/search"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <SearchPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/search-test"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <SearchTest />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/products/:id"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <ProductDetailPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/compare"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <ProductComparePage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          <Route
            path="/locations/:city"
            element={
              <AppWrapper>
                <Header />
                <MainContent>
                  <CityPage />
                </MainContent>
                <Footer />
              </AppWrapper>
            }
          />
          {/* Catch all other routes - show 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ScrollHandler>
    </Router>
    //</ProtectionLayer>
  );
}

export default App;
